import { useEffect, useMemo, useState } from "react";
import { api, money, formatDateTime, buildSistemaWhatsappMessage, openWhatsapp } from "@/lib/api";
import { toast } from "sonner";
import { Check, Plus, Trash2, Edit3, DollarSign, Package, Users, Car, Wrench, MoreHorizontal, Flag, CheckCircle2, AlertTriangle, CreditCard, Copy, RotateCcw } from "lucide-react";

const TIPOS = [
  { key: "material", label: "Material", Icon: Package, color: "bg-amber-100 text-amber-800" },
  { key: "ajudante", label: "Ajudante", Icon: Users, color: "bg-blue-100 text-blue-800" },
  { key: "deslocamento", label: "Deslocamento", Icon: Car, color: "bg-emerald-100 text-emerald-800" },
  { key: "equipamento", label: "Equipamento", Icon: Wrench, color: "bg-purple-100 text-purple-800" },
  { key: "outros", label: "Outros", Icon: MoreHorizontal, color: "bg-zinc-100 text-zinc-700" },
];

export default function ExecucaoTab({ orc, setOrc, cliente, onReload }) {
  const [gastos, setGastos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [finalizando, setFinalizando] = useState(false);
  const [mostrarAvisoCliente, setMostrarAvisoCliente] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editGasto, setEditGasto] = useState(null);

  const carregarGastos = async () => {
    if (!orc.id) return;
    setCarregando(true);
    try {
      const { data } = await api.get(`/orcamentos/${orc.id}/gastos`);
      setGastos(data);
    } catch { toast.error("Erro ao carregar gastos"); }
    finally { setCarregando(false); }
  };

  useEffect(() => { carregarGastos(); /* eslint-disable-next-line */ }, [orc.id]);

  const itens = orc.itens || [];
  const totalItens = itens.length;
  const concluidos = itens.filter((it) => it.concluido).length;
  const pct = totalItens ? Math.round((concluidos / totalItens) * 100) : 0;

  const receita = Number(orc.valor_total || 0);
  const custos = gastos.filter((g) => !g.is_patrimonio).reduce((s, g) => s + Number(g.valor || 0), 0);
  const patrimonio = gastos.filter((g) => g.is_patrimonio).reduce((s, g) => s + Number(g.valor || 0), 0);
  const lucro = receita - custos;
  const margem = receita > 0 ? (lucro / receita) * 100 : 0;

  const toggleItem = async (item) => {
    try {
      const { data } = await api.patch(`/orcamentos/${orc.id}/itens/${item.id}`, { concluido: !item.concluido });
      const novos = itens.map((it) => (it.id === item.id ? { ...it, ...data } : it));
      setOrc({ ...orc, itens: novos });
    } catch (e) { toast.error("Erro ao atualizar item"); }
  };

  const removerGasto = async (g) => {
    if (!window.confirm("Remover este gasto?")) return;
    try {
      await api.delete(`/gastos/${g.id}`);
      setGastos((arr) => arr.filter((x) => x.id !== g.id));
    } catch { toast.error("Erro"); }
  };

  const confirmarFinalizar = async (avisar) => {
    setMostrarAvisoCliente(false);
    setFinalizando(true);
    try {
      await api.post(`/orcamentos/${orc.id}/finalizar`);
      toast.success("Orçamento finalizado");
      onReload && onReload();
      if (avisar) {
        const nomeC = (cliente?.nome || "cliente").trim();
        const msg = buildSistemaWhatsappMessage([
          `Olá ${nomeC}, o serviço do orçamento nº ${orc.numero_orcamento} foi concluído.`,
          "",
          "Agradecemos a confiança! Qualquer dúvida ou feedback, estamos à disposição.",
        ]);
        openWhatsapp(cliente?.whatsapp || cliente?.telefone, msg);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao finalizar");
    } finally { setFinalizando(false); }
  };

  // ===== Pagamento (novo modelo: parcial automático) =====
  const [pagtosData, setPagtosData] = useState(null);
  const [pixOpen, setPixOpen] = useState(false);
  const [pixValor, setPixValor] = useState("");
  const [pixObs, setPixObs] = useState("");

  const loadPagtos = async () => {
    if (!orc.id) return;
    try {
      const { data } = await api.get(`/orcamentos/${orc.id}/pagamentos`);
      setPagtosData(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadPagtos(); /* eslint-disable-next-line */ }, [orc.id, orc.valor_total, orc.status]);

  const pagamentoStatus = pagtosData?.status || orc.pagamento_status || "pendente";
  const totalPago = pagtosData?.total_pago ?? 0;
  const saldo = pagtosData?.saldo ?? (orc.valor_total || 0);
  const transacoes = pagtosData?.transacoes || [];

  const registrarPix = async () => {
    const v = parseFloat((pixValor || "").replace(",", "."));
    if (!v || v <= 0) { toast.error("Informe um valor válido"); return; }
    if (v > saldo + 0.01) { toast.error(`Valor acima do saldo (R$ ${saldo.toFixed(2)})`); return; }
    try {
      await api.post(`/orcamentos/${orc.id}/pagamento/registrar-pix`, {
        valor: v, observacao: pixObs,
      });
      toast.success(`PIX de ${money(v)} registrado`);
      setPixOpen(false); setPixValor(""); setPixObs("");
      await loadPagtos();
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar pagamento");
    }
  };

  const removerPagamento = async (txid) => {
    if (!window.confirm("Remover este pagamento do orçamento?")) return;
    try {
      await api.delete(`/orcamentos/${orc.id}/pagamentos/${txid}`);
      toast.success("Pagamento removido");
      await loadPagtos();
      onReload && onReload();
    } catch (e) { toast.error("Erro ao remover"); }
  };

  const copiarLinkPagto = () => {
    const origin = process.env.REACT_APP_PUBLIC_ORIGIN || window.location.origin;
    const url = `${origin}/pagamento/${orc.token_publico}`;
    navigator.clipboard.writeText(url);
    toast.success("Link de pagamento copiado");
  };

  if (!orc.id) {
    return (
      <div className="bbh-card p-8 text-center text-sm text-zinc-500">
        Salve o orçamento primeiro para acessar a tela de execução.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="execucao-tab">
      {/* Resumo financeiro */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Receita" valor={money(receita)} tone="indigo" />
        <StatCard label="Custos" valor={money(custos)} tone="rose" />
        <StatCard label="Lucro" valor={money(lucro)} tone={lucro >= 0 ? "emerald" : "rose"} />
        <StatCard label="Margem" valor={`${margem.toFixed(1)}%`} tone={margem >= 0 ? "emerald" : "rose"} />
      </section>
      {patrimonio > 0 && (
        <div className="bbh-card p-3 flex items-center gap-2 text-sm text-indigo-800 bg-indigo-50 border-indigo-200">
          <Flag className="w-4 h-4" />
          <span>
            <b>{money(patrimonio)}</b> alocado como patrimônio/investimento (não entra no custo direto)
          </span>
        </div>
      )}

      {/* Progresso */}
      <section className="bbh-card p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Progresso de execução</div>
            <div className="font-heading text-lg font-bold">{concluidos} de {totalItens} concluído(s)</div>
          </div>
          <span className="font-heading text-2xl font-bold">{pct}%</span>
        </div>
        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>

        <div className="mt-4 space-y-2">
          {itens.map((it) => (
            <ItemConclusao key={it.id} item={it} onToggle={() => toggleItem(it)} />
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-zinc-500">
            {orc.status === "finalizado"
              ? <span className="inline-flex items-center gap-1 text-indigo-700"><CheckCircle2 className="w-3.5 h-3.5" /> Orçamento já finalizado em {formatDateTime(orc.data_finalizacao)}</span>
              : orc.status === "aprovado"
              ? (pct === 100
                  ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3.5 h-3.5" /> Tudo pronto para finalizar</span>
                  : <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> Conclua todos os itens para liberar o botão Finalizar</span>)
              : <span>Status: {orc.status}. Finalizar só disponível quando o orçamento estiver aprovado.</span>
            }
          </div>
          <button
            onClick={() => setMostrarAvisoCliente(true)}
            disabled={orc.status !== "aprovado" || pct < 100 || finalizando}
            data-testid="finalizar-exec-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCircle2 className="w-4 h-4" /> Finalizar orçamento
          </button>
        </div>
      </section>

      {/* Pagamento */}
      <section className="bbh-card p-5" data-testid="pagamento-section">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Pagamento</div>
            <div className="font-heading text-lg font-bold flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#14243D]" />
              {pagamentoStatus === "pago"
                ? <span className="text-emerald-700">Quitado</span>
                : pagamentoStatus === "parcial"
                ? <span className="text-amber-700">Pago parcial — falta {money(saldo)}</span>
                : <span className="text-zinc-700">Aguardando pagamento</span>}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {money(totalPago)} de {money(orc.valor_total || 0)} recebido(s)
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copiarLinkPagto} data-testid="copiar-link-pagto-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50">
              <Copy className="w-4 h-4" /> Copiar link
            </button>
            {saldo > 0 && (
              <button onClick={() => { setPixValor(saldo.toFixed(2)); setPixOpen(true); }} data-testid="registrar-pix-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
                <DollarSign className="w-4 h-4" /> Registrar PIX
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {(orc.valor_total || 0) > 0 && (
          <div className="h-2 rounded-full overflow-hidden bg-zinc-100 mb-3">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, Math.round((totalPago / (orc.valor_total || 1)) * 100))}%` }} />
          </div>
        )}

        {/* Histórico transações */}
        {transacoes.length > 0 ? (
          <div className="space-y-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Histórico</div>
            <ul className="space-y-1">
              {transacoes.map((t) => (
                <li key={t.session_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-zinc-50 border border-zinc-100 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${t.payment_status === "paid" ? "bg-emerald-500" : t.payment_status === "pending" ? "bg-amber-500" : "bg-zinc-300"}`} />
                    <div className="truncate">
                      <strong>{money(t.valor_credito || t.base_amount || 0)}</strong>
                      <span className="text-zinc-500 text-xs ml-1">
                        · {t.metodo === "pix" ? "PIX" : `Cartão ${t.parcelas || 1}x`}
                        {t.payment_status !== "paid" ? ` · ${t.payment_status}` : ""}
                      </span>
                      {t.observacao && <div className="text-xs text-zinc-500 truncate">{t.observacao}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-zinc-500">
                      {t.paid_at ? formatDateTime(t.paid_at) : (t.created_at ? formatDateTime(t.created_at) : "")}
                    </span>
                    {t.payment_status === "paid" && (
                      <button onClick={() => removerPagamento(t.session_id)} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500" title="Remover pagamento">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-xs text-zinc-500 py-3 text-center">
            Nenhum pagamento recebido ainda. O botão de pagar aparece pro cliente assim que ele aprovar o orçamento.
          </div>
        )}
      </section>

      {/* Modal registrar PIX */}
      {pixOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setPixOpen(false)}>
          <div className="bbh-card bg-white p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="font-heading text-xl font-bold mb-1">Registrar PIX recebido</div>
            <div className="text-xs text-zinc-500 mb-3">Saldo atual: <strong>{money(saldo)}</strong></div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Valor recebido</label>
            <div className="flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-3 mt-1">
              <span className="text-zinc-500 text-sm">R$</span>
              <input type="text" inputMode="decimal" value={pixValor} onChange={(e) => setPixValor(e.target.value.replace(/[^\d.,]/g, ""))} placeholder={saldo.toFixed(2).replace(".", ",")} data-testid="pix-valor-input" className="flex-1 py-2 bg-transparent outline-none text-sm" autoFocus />
            </div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mt-3 block">Observação (opcional)</label>
            <input type="text" value={pixObs} onChange={(e) => setPixObs(e.target.value)} placeholder="Ex: entrada, 2ª parcela..." data-testid="pix-obs-input" className="w-full mt-1 px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setPixOpen(false)} className="flex-1 py-2.5 rounded-md border border-zinc-200 font-medium">Cancelar</button>
              <button onClick={registrarPix} data-testid="confirmar-pix-btn" className="flex-1 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* Gastos */}
      <section className="bbh-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Gastos deste orçamento</div>
            <div className="font-heading text-lg font-bold">{gastos.length} lançamento(s)</div>
          </div>
          <button
            onClick={() => setAddOpen(true)}
            data-testid="add-gasto-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bbh-btn-primary text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Novo gasto
          </button>
        </div>
        {carregando ? (
          <div className="text-sm text-zinc-500">Carregando…</div>
        ) : gastos.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">
            Nenhum gasto lançado ainda. Adicione materiais, ajudantes, deslocamento etc.
          </div>
        ) : (
          <div className="space-y-2">
            {gastos.map((g) => (
              <GastoRow key={g.id} gasto={g} onEdit={() => setEditGasto(g)} onRemove={() => removerGasto(g)} />
            ))}
          </div>
        )}
      </section>

      {addOpen && <GastoForm orcId={orc.id} onClose={() => setAddOpen(false)} onCreated={(g) => { setGastos((arr) => [...arr, g]); setAddOpen(false); }} />}
      {editGasto && <GastoForm orcId={orc.id} gasto={editGasto} onClose={() => setEditGasto(null)} onCreated={(g) => { setGastos((arr) => arr.map((x) => (x.id === g.id ? g : x))); setEditGasto(null); }} />}
      {mostrarAvisoCliente && (
        <ConfirmAvisarCliente
          onCancel={() => setMostrarAvisoCliente(false)}
          onNao={() => confirmarFinalizar(false)}
          onSim={() => confirmarFinalizar(true)}
          finalizando={finalizando}
        />
      )}
    </div>
  );
}

function StatCard({ label, valor, tone = "emerald" }) {
  const tones = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-200",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[tone]}`}>
      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-heading text-xl font-bold mt-1">{valor}</div>
    </div>
  );
}

function ItemConclusao({ item, onToggle }) {
  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-md border ${item.concluido ? "border-emerald-200 bg-emerald-50" : "border-zinc-200 bg-white"}`}>
      <button
        onClick={onToggle}
        data-testid={`toggle-concluido-${item.id}`}
        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition ${
          item.concluido ? "bg-emerald-500 border-emerald-500 text-white" : "border-zinc-300 bg-white"
        }`}
        aria-label={item.concluido ? "Desmarcar" : "Marcar como concluído"}
      >
        {item.concluido && <Check className="w-3.5 h-3.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium whitespace-pre-wrap ${item.concluido ? "line-through text-zinc-500" : ""}`}>{item.descricao}</div>
        {item.concluido && item.concluido_em && (
          <div className="text-[10px] text-emerald-700">Concluído em {formatDateTime(item.concluido_em)}</div>
        )}
      </div>
    </div>
  );
}

function GastoRow({ gasto, onEdit, onRemove }) {
  const t = TIPOS.find((x) => x.key === gasto.tipo) || TIPOS[4];
  return (
    <div className={`flex items-center gap-3 p-3 rounded-md border ${gasto.is_patrimonio ? "border-indigo-200 bg-indigo-50/50" : "border-zinc-200 bg-white"}`} data-testid={`gasto-${gasto.id}`}>
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide shrink-0 ${t.color}`}>
        <t.Icon className="w-3 h-3" />
        {t.label}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{gasto.descricao}</div>
        <div className="text-[11px] text-zinc-500">
          {formatDateTime(gasto.data)}
          {gasto.is_patrimonio && <span className="ml-2 text-indigo-700 font-semibold">· Patrimônio</span>}
        </div>
      </div>
      <div className="font-heading font-bold text-sm shrink-0">{money(gasto.valor)}</div>
      <button onClick={onEdit} title="Editar gasto" data-testid={`edit-gasto-${gasto.id}`} className="p-1.5 text-zinc-400 hover:text-[#14243D] hover:bg-zinc-100 rounded">
        <Edit3 className="w-4 h-4" />
      </button>
      <button onClick={onRemove} title="Remover gasto" data-testid={`remove-gasto-${gasto.id}`} className="p-1.5 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function GastoForm({ orcId, gasto, onClose, onCreated }) {
  const [tipo, setTipo] = useState(gasto?.tipo || "material");
  const [descricao, setDescricao] = useState(gasto?.descricao || "");
  const [valor, setValor] = useState(gasto ? String(gasto.valor).replace(".", ",") : "");
  const [isPatrimonio, setIsPatrimonio] = useState(!!gasto?.is_patrimonio);
  const [observacao, setObservacao] = useState(gasto?.observacao || "");
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!descricao.trim()) return toast.error("Descrição é obrigatória");
    const v = parseFloat(String(valor).replace(",", "."));
    if (isNaN(v) || v <= 0) return toast.error("Valor inválido");
    setSalvando(true);
    try {
      const body = {
        tipo, descricao: descricao.trim(), valor: v,
        is_patrimonio: isPatrimonio,
        observacao: observacao.trim() || null,
      };
      if (gasto) {
        await api.patch(`/gastos/${gasto.id}`, body);
        toast.success("Gasto atualizado");
        onCreated({ ...gasto, ...body });
      } else {
        const { data } = await api.post(`/orcamentos/${orcId}/gastos`, body);
        toast.success("Gasto registrado");
        onCreated(data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-xl space-y-3" onClick={(e) => e.stopPropagation()} data-testid="gasto-form">
        <div className="font-heading text-lg font-bold">{gasto ? "Editar gasto" : "Novo gasto"}</div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Tipo</label>
          <div className="mt-1 grid grid-cols-5 gap-1">
            {TIPOS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTipo(t.key)}
                data-testid={`tipo-${t.key}`}
                className={`flex flex-col items-center gap-1 py-2 px-1 rounded-md border text-[10px] font-semibold ${
                  tipo === t.key ? "border-[#14243D] bg-[#14243D]/5" : "border-zinc-200 text-zinc-500"
                }`}
              >
                <t.Icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Descrição</label>
          <input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex: 10 saquinhos de cimento" data-testid="gasto-desc" className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Valor (R$)</label>
            <input type="text" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="0,00" data-testid="gasto-valor" className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-700 mt-5">
            <input type="checkbox" checked={isPatrimonio} onChange={(e) => setIsPatrimonio(e.target.checked)} data-testid="gasto-patrimonio" className="w-4 h-4 accent-indigo-600" />
            Patrimônio/Investimento
          </label>
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Observação (opcional)</label>
          <textarea rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} data-testid="gasto-obs" className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm" />
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-700">Cancelar</button>
          <button onClick={salvar} disabled={salvando} data-testid="salvar-gasto-btn" className="flex-1 px-4 py-2 rounded-md bbh-btn-primary text-sm font-semibold disabled:opacity-60">{salvando ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmAvisarCliente({ onCancel, onNao, onSim, finalizando }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl w-full max-w-sm p-5 shadow-xl space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="font-heading text-lg font-bold">Finalizar orçamento</div>
        <p className="text-sm text-zinc-600">Deseja enviar um aviso ao cliente pelo WhatsApp informando que o serviço foi concluído?</p>
        <div className="flex gap-2 pt-2">
          <button onClick={onCancel} disabled={finalizando} className="flex-1 px-3 py-2 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-700">Cancelar</button>
          <button onClick={onNao} disabled={finalizando} data-testid="finalizar-sem-aviso" className="flex-1 px-3 py-2 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-700">Não</button>
          <button onClick={onSim} disabled={finalizando} data-testid="finalizar-com-aviso" className="flex-1 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">Sim, enviar</button>
        </div>
      </div>
    </div>
  );
}
